import React from "react";
import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "October 28, 2026";
const CONTACT_EMAIL = "hello@sportifide.com";
// Replace with the registered company name once Sportifide is incorporated.
const OPERATOR = "Sportifide";

export default function PrivacyPolicy() {
  const mail = <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a>;
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <Link to="/login" style={styles.back}>← Back to Login</Link>
        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.meta}>Effective date: {EFFECTIVE_DATE} · Sportifide</p>

        <Section title="1. Who We Are">
          <p>Sportifide ("we," "us," or "our") is a sports platform for players, league and
          tournament organisers, clubs, and schools. It is operated by {OPERATOR} from Ontario,
          Canada. This Privacy Policy explains what personal information we collect, why, who we
          share it with, and the choices and rights you have.</p>
          <p>We follow Canada's Personal Information Protection and Electronic Documents Act
          (PIPEDA) and, for people in India, the Digital Personal Data Protection Act, 2023 and
          its Rules (the "DPDP Act"). Our privacy officer can be reached at {mail}.</p>
        </Section>

        <Section title="2. Personal Accounts and School or Organisation Spaces">
          <p><strong>Personal accounts:</strong> when you sign up yourself, we decide how your
          information is used, as described in this policy.</p>
          <p><strong>School and organisation spaces:</strong> when a school, club, or other
          organisation uses Sportifide to manage its own students, members, or staff, that
          organisation decides what information is entered and why. For that information the
          organisation is responsible for it (under the DPDP Act, the "data fiduciary") and we
          process it only on the organisation's instructions and under our agreement with it
          (as its service provider or "data processor"). Questions about those records should go
          to the organisation first; we will help it respond.</p>
        </Section>

        <Section title="3. Information We Collect">
          <p><strong>Account information:</strong> name, email address, username, and password
          (stored only as a cryptographic hash). If you sign in with Google, Facebook, or a phone
          number, we receive your name, email address or phone number from that service.</p>
          <p><strong>Profile data (optional):</strong> profile photo, date of birth, phone number,
          sport preferences, skill levels, and a general location or city.</p>
          <p><strong>Activity data:</strong> sports events you create or join, match scores and
          statistics, league and tournament participation, team memberships, friends, blocked or
          muted users, and messages sent within the Platform.</p>
          <p><strong>Location:</strong> if you allow it, your approximate location is used to show
          nearby events and venues. You can turn this off in your browser or device settings.</p>
          <p><strong>School and organisation records:</strong> information an organisation enters
          about its members, such as student name, class or grade, house or team, sports results,
          fitness test results, and staff contact emails. We ask schools not to enter government ID
          numbers (such as Aadhaar or SIN), home addresses, or medical information.</p>
          <p><strong>Payment information:</strong> when paid features are offered, payments are
          handled by our payment processor (Stripe). We receive confirmation of the payment and
          limited billing details; we never see or store full card numbers.</p>
          <p><strong>Technical data:</strong> IP address, browser type, device identifiers,
          session tokens, request logs, and error reports collected automatically.</p>
          <p><strong>Communications:</strong> messages you send us by email or the feedback
          widget, and your email address.</p>
        </Section>

        <Section title="4. How We Use Your Information">
          <ul>
            <li>To create and manage your account and keep you signed in.</li>
            <li>To run the sports features: events, leagues, tournaments, teams, scoring,
            standings, statistics, and messages.</li>
            <li>To provide school and organisation spaces on behalf of the organisation.</li>
            <li>To send service emails (account activation, password reset, match and league
            updates). You can turn off non-essential notifications.</li>
            <li>To process payments and keep business and tax records.</li>
            <li>To answer support requests and feedback.</li>
            <li>To keep the Platform safe: detect fraud, abuse, and security incidents.</li>
            <li>To fix bugs and improve the Platform using aggregated or de-identified data.</li>
          </ul>
          <p>We rely on your consent, or the consent obtained by your school or organisation,
          for these purposes, and on legal obligations where the law requires us to keep or
          disclose information. You may withdraw consent at any time (see section 10); some
          features may then stop working.</p>
          <p>We do <strong>not</strong> sell personal data, we do <strong>not</strong> show
          advertising, and we do <strong>not</strong> use anyone's data for targeted advertising.</p>
        </Section>

        <Section title="5. Who We Share It With">
          <p><strong>Other users:</strong> your username, profile photo, and sports activity
          (teams, matches, leagues) are visible to other users as part of the community features.
          Your email address and phone number are never shown publicly. Records in a school or
          organisation space are visible only to people that organisation authorises.</p>
          <p><strong>Service providers:</strong> we use trusted providers to run the Platform:
          website and server hosting (Vercel, Render), database and storage (Supabase), email
          delivery (Brevo, SendGrid), sign-in (Google, Facebook), error monitoring (Sentry), and
          payments (Stripe). They may process data only to provide their service to us, under
          contracts that require them to protect it.</p>
          <p><strong>Legal requirements:</strong> we may disclose information if required by
          law, court order, or a lawful government request, or to protect the safety of any
          person.</p>
          <p><strong>Business transfer:</strong> if Sportifide is reorganised, merged, or sold,
          personal information may transfer to the new owner, who must keep protecting it under
          this policy.</p>
        </Section>

        <Section title="6. Where Your Data Is Stored">
          <p>Our providers store and process data in Canada, the United States, and other
          countries where they operate. Data stored outside your country may be accessible to
          that country's courts and authorities under its laws. We transfer data only to
          countries not restricted by the laws that apply to us, and we choose providers with
          strong security practices.</p>
        </Section>

        <Section title="7. Cookies &amp; Similar Technologies">
          <p>We use HTTP-only cookies to manage signed-in sessions securely. These cookies are
          essential for the Platform to work and are not used for advertising or cross-site
          tracking. We also use your browser's local storage for non-sensitive preferences (for
          example, your last selected sport filter).</p>
        </Section>

        <Section title="8. How Long We Keep It">
          <p>We keep account data while your account is active. If you delete your account, your
          personal information is removed within 30 days, except where we must keep it longer by
          law (for example, payment and tax records) or to resolve a dispute.</p>
          <p>School and organisation records are kept for as long as the organisation's agreement
          with us lasts, then deleted or returned to the organisation, as it chooses.</p>
          <p>Aggregated or de-identified data (for example, match statistics with no personal
          details) may be kept for analytics.</p>
        </Section>

        <Section title="9. Security">
          <p>We use reasonable security safeguards, including:</p>
          <ul>
            <li>Passwords hashed with bcrypt.</li>
            <li>HTTPS/TLS encryption in transit and encrypted database storage.</li>
            <li>HTTP-only, Secure session cookies and CSRF protection.</li>
            <li>Rate limiting on sign-in, role-based access, and audit logs for changes.</li>
          </ul>
          <p>No system is perfectly secure. If a breach of your personal information creates a
          real risk of significant harm, or is otherwise reportable, we will notify you and the
          relevant regulator (including the Office of the Privacy Commissioner of Canada and the
          Data Protection Board of India) as the law requires. Report security concerns to {mail}.</p>
        </Section>

        <Section title="10. Your Rights">
          <p>Depending on where you live, you have the right to:</p>
          <ul>
            <li><strong>Access:</strong> ask what personal data we hold about you and get a copy.</li>
            <li><strong>Correction:</strong> fix inaccurate data, in your Profile settings or by asking us.</li>
            <li><strong>Deletion:</strong> delete your account from Profile settings, or ask us to erase data.</li>
            <li><strong>Withdraw consent:</strong> stop optional uses of your data at any time.</li>
            <li><strong>Portability:</strong> ask for an export of your data in a machine-readable format.</li>
            <li><strong>Nominate:</strong> (India) name someone to exercise your rights if you die or cannot act.</li>
            <li><strong>Complain:</strong> raise a grievance with us first; we reply within 30 days.</li>
          </ul>
          <p>To use any of these rights, email {mail}. We may need to confirm your identity. If you
          are not satisfied with our answer, you can complain to the{" "}
          <a href="https://www.priv.gc.ca" style={styles.link} target="_blank" rel="noopener noreferrer">Office
          of the Privacy Commissioner of Canada</a> or, in India, the Data Protection Board of India.</p>
        </Section>

        <Section title="11. Children and Students">
          <p>You must be at least 13 to create your own Sportifide account. If you are 13 to 17, a
          parent or legal guardian must agree to your use of Sportifide, and we may ask for
          confirmation. In India, anyone under 18 is a child under the DPDP Act, so people in India
          under 18 may use Sportifide only with verifiable consent from a parent or guardian.</p>
          <p>Younger students may appear on Sportifide only through their school or a youth
          organisation, which is responsible for obtaining parent or guardian consent before
          entering their information. Student accounts and parent accounts are enabled only after
          that consent is in place.</p>
          <p>For anyone under 18 we do not track behaviour across sites, build profiles for
          advertising, or show ads, and we collect only what is needed to run their sports
          activities. If you believe a child's information was provided without proper consent,
          contact {mail} and we will delete it promptly.</p>
        </Section>

        <Section title="12. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. We will tell registered users
          by email or an in-app notice before material changes take effect. The effective date at
          the top of this page always shows the latest version.</p>
        </Section>

        <Section title="13. Contact Us">
          <p>Privacy officer and grievance contact: {mail}</p>
          <p>{OPERATOR}, Ontario, Canada</p>
        </Section>

        <footer style={styles.footer}>
          © {new Date().getFullYear()} Sportifide. All rights reserved.
        </footer>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: "1.75rem" }}>
      <h2 style={styles.sectionTitle}>{title}</h2>
      <div style={styles.body}>{children}</div>
    </section>
  );
}

const styles = {
  page: {
    position: "fixed",
    inset: 0,
    overflowY: "auto",
    overflowX: "hidden",
    background: "linear-gradient(135deg, #0f1729 0%, #1a2640 100%)",
    padding: "2rem 1rem",
    fontFamily: "Arial, sans-serif",
  },
  card: {
    maxWidth: 760,
    margin: "0 auto",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 16,
    padding: "2.5rem 2rem",
    color: "#e0e6f0",
  },
  back: {
    display: "inline-block",
    marginBottom: "1.5rem",
    color: "#fd7e14",
    textDecoration: "none",
    fontSize: "0.9rem",
  },
  title: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#fff",
    margin: "0 0 0.25rem",
  },
  meta: {
    color: "rgba(255,255,255,0.45)",
    fontSize: "0.85rem",
    marginBottom: "2rem",
  },
  sectionTitle: {
    fontSize: "1.05rem",
    fontWeight: 700,
    color: "#fd7e14",
    marginBottom: "0.5rem",
  },
  body: {
    fontSize: "0.92rem",
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.8)",
  },
  link: { color: "#fd7e14" },
  footer: {
    marginTop: "2.5rem",
    paddingTop: "1rem",
    borderTop: "1px solid rgba(255,255,255,0.1)",
    textAlign: "center",
    fontSize: "0.82rem",
    color: "rgba(255,255,255,0.35)",
  },
};
